import React from 'react';
import { Helmet } from 'react-helmet-async';
import { FileText, Phone, Mail, Shield, Bell, UserCheck, AlertCircle } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';

const TermsAndConditionsPage = () => {
  return (
    <div className="min-h-screen bg-gray-50" data-testid="terms-page">
      <Helmet>
        <title>Terms and Conditions | RO Dekho</title>
        <meta name="description" content="Terms and Conditions for RO Dekho by Arpsan Lifestyle Technology Pvt Ltd. Read about data usage, communication consent, and service policies." />
        <link rel="canonical" href="https://www.rodekho.in/terms-and-conditions" />
      </Helmet>

      <Header />

      <div className="container mx-auto px-4 py-12 md:py-16 max-w-4xl">
        {/* Page Header */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-100 rounded-2xl mb-4">
            <FileText size={32} className="text-blue-700" />
          </div>
          <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-3" data-testid="terms-title">
            Terms and Conditions
          </h1>
          <p className="text-gray-500 text-sm">
            Last updated: {new Date().toLocaleDateString('en-IN', { year: 'numeric', month: 'long', day: 'numeric' })}
          </p>
          <p className="text-gray-600 mt-2">
            RO Dekho &mdash; A venture of <strong>Arpsan Lifestyle Technology Pvt Ltd</strong>
          </p>
        </div>

        {/* Content */}
        <div className="bg-white rounded-2xl shadow-sm border p-6 md:p-10 space-y-8">

          {/* Consent Section */}
          <section>
            <div className="flex items-start gap-3 mb-4">
              <div className="p-2 bg-green-100 rounded-lg flex-shrink-0">
                <UserCheck size={20} className="text-green-700" />
              </div>
              <h2 className="text-xl font-bold text-gray-900" data-testid="section-consent">
                Consent to Data Collection and Communication
              </h2>
            </div>
            <p className="text-gray-700 leading-relaxed">
              By accepting the Terms and Conditions of our platform, you expressly consent to our collection and use of your personal data, specifically your phone number, for the purpose of contacting you through phone calls, IVR and RCS messaging.
            </p>
          </section>

          {/* Purpose of Communication */}
          <section>
            <div className="flex items-start gap-3 mb-4">
              <div className="p-2 bg-blue-100 rounded-lg flex-shrink-0">
                <Bell size={20} className="text-blue-700" />
              </div>
              <h2 className="text-xl font-bold text-gray-900" data-testid="section-purpose">
                Purpose of Communication
              </h2>
            </div>
            <p className="text-gray-700 leading-relaxed mb-4">
              These calls and messages may be made to:
            </p>
            <ul className="space-y-2 ml-4">
              {[
                'Remind you of any relevant updates, activities, or information regarding your account',
                'Inform you about new offers, promotions, or updates related to our platform and services',
                'Provide order status updates, booking confirmations, and service reminders',
                'Share important service-related information essential to your use of the platform'
              ].map((item, i) => (
                <li key={i} className="flex items-start gap-2 text-gray-700">
                  <span className="w-1.5 h-1.5 bg-blue-600 rounded-full mt-2 flex-shrink-0" />
                  {item}
                </li>
              ))}
            </ul>
          </section>

          {/* Acknowledgement */}
          <section>
            <div className="flex items-start gap-3 mb-4">
              <div className="p-2 bg-violet-100 rounded-lg flex-shrink-0">
                <Shield size={20} className="text-violet-700" />
              </div>
              <h2 className="text-xl font-bold text-gray-900" data-testid="section-acknowledgement">
                Your Acknowledgement
              </h2>
            </div>
            <p className="text-gray-700 leading-relaxed">
              You acknowledge and agree that we may use your phone number to provide these communications and understand that this is a necessary part of your engagement with our platform.
            </p>
          </section>

          {/* Opt-Out */}
          <section>
            <div className="flex items-start gap-3 mb-4">
              <div className="p-2 bg-amber-100 rounded-lg flex-shrink-0">
                <AlertCircle size={20} className="text-amber-700" />
              </div>
              <h2 className="text-xl font-bold text-gray-900" data-testid="section-optout">
                Opt-Out Policy
              </h2>
            </div>
            <p className="text-gray-700 leading-relaxed mb-4">
              You may opt out of receiving such communications at any time by contacting our support team or following the opt-out instructions provided in any communication.
            </p>
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
              <p className="text-amber-800 text-sm font-medium">
                Please note: Opting out of promotional calls will not affect non-promotional or service-related communications that are essential to your use of the platform.
              </p>
            </div>
          </section>

          {/* Contact */}
          <section className="border-t pt-8">
            <h2 className="text-xl font-bold text-gray-900 mb-4">Contact Us</h2>
            <p className="text-gray-700 mb-4">
              For any questions regarding these terms, please reach out to us:
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <a href="tel:+919887068888"
                className="inline-flex items-center gap-2 px-5 py-3 bg-blue-50 border border-blue-200 rounded-xl text-blue-700 font-medium hover:bg-blue-100 transition-colors">
                <Phone size={18} /> +91 9887068888
              </a>
              <a href="mailto:support@rodekho.com"
                className="inline-flex items-center gap-2 px-5 py-3 bg-gray-50 border border-gray-200 rounded-xl text-gray-700 font-medium hover:bg-gray-100 transition-colors">
                <Mail size={18} /> support@rodekho.com
              </a>
            </div>
          </section>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default TermsAndConditionsPage;
